import type { GetStaticPaths } from "next"

import { DocumentType } from "~/api/prismic/enums"
import { queryDocumentUIDsByType } from "~/api/prismic/graphql/documents/queryDocumentsByType"
import { linkResolver } from "~/api/prismic/linkResolver"
import { getContentTheme } from "~/api/prismic/themes/getContentThemes"
import type {
  AuthorDetails,
  ContentThemePageProps as PageProps,
} from "~/screens/ContentTheme"
import { ContentThemePage } from "~/screens/ContentTheme"
import { AUTHOR_QUERY_PARAM } from "~/screens/ContentTheme/constants"
import { withLoadingFallback } from "~/utils/hocs/withLoadingFallback"
import { imageOrFallback } from "~/utils/images/imageOrFallback"
import { enhanceStaticProps } from "~/utils/next/enhanceStaticProps"
import { getPreviewRef } from "~/utils/prismic/previewData"
import { withoutFalsyValues } from "~/utils/withoutFalsyValues"

type PathParams = {
  uid: string
}

export const getStaticPaths: GetStaticPaths<PathParams> = async () => {
  const uids = await queryDocumentUIDsByType({
    pageSize: 20,
    type: DocumentType.CONTENT_THEME,
  })

  return {
    paths: uids.map((uid) => ({ params: { uid } })),
    // Always pre-render new articles
    fallback: "blocking",
  }
}

export const getStaticProps = enhanceStaticProps<PageProps, PathParams>(
  async ({ params, previewData }) => {
    const uid = params?.uid

    const pathname = linkResolver({
      type: DocumentType.CONTENT_THEME,
      uid,
    })

    if (!uid) {
      return { notFound: true }
    }

    const contentTheme = await getContentTheme({
      uid,
      ref: getPreviewRef(previewData),
    })

    if (!contentTheme) {
      return { notFound: true }
    }
    const { data } = contentTheme

    const authors = data.authors.map<AuthorDetails | null>(
      ({ author, connection: connectionLink }) => {
        if (
          !("uid" in author) ||
          !author.uid ||
          author.isBroken ||
          !author.data
        ) {
          return null
        }
        const connection =
          "uid" in connectionLink &&
          connectionLink?.data?.name &&
          connectionLink.uid
            ? {
                name: connectionLink.data.name,
                href: {
                  pathname,
                  query: { [AUTHOR_QUERY_PARAM]: connectionLink.uid },
                },
              }
            : null

        return {
          id: author.id,
          uid: author.uid,
          bio: author.data.bio || [],
          connection,
          name: author.data.name || "",
          contacts: {
            instagram: author.data.instagram || null,
            twitter: author.data.twitter_handle || null,
          },
          profilePic: imageOrFallback(author.data.profile_picture?.url),
        }
      },
    )

    const props: PageProps = {
      authors: withoutFalsyValues(authors),
      bannerImage: imageOrFallback(data.banner_image.url),
      title: data.name,
      heading: data.heading,
    }

    return {
      props,
      revalidate: 15,
    }
  },
)

export default withLoadingFallback(ContentThemePage)
